// AlertBell — login-notifications dropdown chrome.
// Source of truth: Documents/AlertBell_DesignDoc.md (§3, §4, §10).
//
// Mounted as the 6th item inside BottomNav (src/components/BottomNav.js).
// The component renders a nav-item-styled button (icon + label) plus an
// upward-opening dropdown. Single source of bottom chrome — no per-page
// edits needed. Deviation from design doc §3.1's "top-right of the
// header" wording, accepted because the project has no shared app
// header and a screenshot pass found ~half the pages had the previous
// top-right placement overlapping page action buttons.
//
// Reads from the `notifications` table (RLS-scoped to the calling user
// via mig 17's notifications_select policy). Never inserts; insertion
// lives in src/utils/notifications.js (createNotifications helper).
//
// Mark-as-read paths (§4.2):
//   - Click on an item → mark THAT item read, navigate, close dropdown.
//   - "Mark all read" button → batch-update unread items, badge → 0.
//   - Opening the bell does NOT mark anything read.
//
// Out of scope for Session 1 (per task brief):
//   - Feature A (mark-read on detail-page visit anywhere in app).
//     Will be added in Session 2 across each detail-page component.
//   - "View all" footer + dedicated What's-New page (§3.3, §12.3).
//     For now we cap at 25 items and show a small "older items hidden"
//     note when the cap is hit.
//   - Realtime subscriptions. We re-fetch on mount, on bell-open, and
//     after mark-read mutations.
//
// Z-index discipline:
//   - The bell button inherits BottomNav's z-40 by virtue of living
//     inside it.
//   - Dropdown at z-50 so it floats above page content + the BottomNav
//     itself when open. Equal to modals (z-50); since modals open with
//     full-screen backdrops, they'd cover the dropdown geometrically
//     anyway when both are open.

import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../supabaseClient'
import { useAuth } from '../context/AuthContext'
import { relativeTime } from '../utils/relativeTime'

const ITEM_CAP = 25
const RETENTION_DAYS = 30

// Per design doc §10.3 — emoji per event type.
const EVENT_ICON = {
  session:       '📋',
  activity:      '🎯',
  nucleus_added: '🌿',
  registration:  '📝',
  participant:   '👤',
  comment_reply: '💬',
  campaign:      '📣',
  reimbursement_requested: '💵',
  in_kind_contributed:     '🎁',
}

// Per design doc §10.4 — click target per event type. event_subject is the
// aggregation key (activity_id for registration, nucleus_id for participant).
function navTargetFor(n) {
  switch (n.event_type) {
    case 'session':       return `/session/${n.event_id}`
    case 'activity':      return `/activity/${n.event_id}`
    case 'nucleus_added': return `/nucleus/${n.event_id}`
    case 'registration':  return `/activity/${n.event_subject || n.event_id}`
    case 'participant':   return `/nucleus/${n.event_subject || n.event_id}`
    case 'comment_reply': return `/celebrations`     // Detail-page route TBD
    case 'campaign':      return `/campaigns/${n.event_id}`
    case 'reimbursement_requested': return `/reimbursements`
    case 'in_kind_contributed':     return `/reimbursements`
    default:              return null
  }
}

export default function AlertBell() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [items, setItems] = useState([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [loading, setLoading] = useState(false)
  const wrapperRef = useRef(null)
  // Optimistic-update race guard. When markAllRead / markRead fires its
  // optimistic setUnreadCount, it stamps this ref with `Date.now()`. Any
  // fetchAll still in flight (e.g., the one that fired on bell-open)
  // whose started_at is earlier than the stamp will see a stale unread
  // count in the DB (the UPDATE hasn't landed yet) and would stomp the
  // optimistic 0 back to the stale value. The guard skips that overwrite.
  const optimisticReadStampRef = useRef(0)

  const fetchAll = useCallback(async () => {
    if (!user?.id) return
    const fetchStartedAt = Date.now()
    setLoading(true)
    const sinceIso = new Date(Date.now() - RETENTION_DAYS * 86400000).toISOString()

    // Two reads in parallel: dropdown items + unread count.
    // Items are ALL recent (read + unread, capped at 25). Count is unread only.
    const [itemsRes, countRes] = await Promise.all([
      supabase
        .from('notifications')
        .select('id, event_type, event_id, event_subject, message, created_at, read_at')
        .eq('profile_id', user.id)
        .gte('created_at', sinceIso)
        .order('created_at', { ascending: false })
        .limit(ITEM_CAP),
      supabase
        .from('notifications')
        .select('id', { count: 'exact', head: true })
        .eq('profile_id', user.id)
        .gte('created_at', sinceIso)
        .is('read_at', null),
    ])

    if (itemsRes.error) console.warn('AlertBell items fetch failed:', itemsRes.error)
    if (countRes.error) console.warn('AlertBell count fetch failed:', countRes.error)
    setItems(itemsRes.data || [])
    // Skip the unread-count overwrite if a Mark-as-read action stamped
    // the ref after this fetch started — its optimistic 0 / decrement
    // is the correct value; the DB count is still stale.
    if (optimisticReadStampRef.current < fetchStartedAt) {
      setUnreadCount(countRes.count || 0)
    }
    setLoading(false)
  }, [user?.id])

  // Initial fetch on mount + on user change.
  useEffect(() => { fetchAll() }, [fetchAll])

  // Re-fetch when the user opens the bell (catches notifications inserted
  // since mount). No realtime subscription in Session 1.
  useEffect(() => { if (open) fetchAll() }, [open, fetchAll])

  // Click outside (or on the page) to close. The wrapper contains both the
  // bell button and the dropdown via DOM-tree parentage — `contains()`
  // returns true even though the dropdown uses fixed positioning.
  useEffect(() => {
    if (!open) return
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const markRead = async (id) => {
    // Optimistic UI; reconcile with the server count if the update fails.
    optimisticReadStampRef.current = Date.now()
    setItems(prev => prev.map(x => x.id === id && !x.read_at
      ? { ...x, read_at: new Date().toISOString() } : x))
    setUnreadCount(prev => Math.max(0, prev - 1))
    const { error } = await supabase
      .from('notifications').update({ read_at: new Date().toISOString() })
      .eq('id', id).is('read_at', null)
    if (error) {
      optimisticReadStampRef.current = 0
      console.warn('markRead failed; re-fetching to reconcile', error)
      await fetchAll()
    }
  }

  const markAllRead = async () => {
    optimisticReadStampRef.current = Date.now()
    const nowIso = new Date().toISOString()
    setItems(prev => prev.map(x => x.read_at ? x : { ...x, read_at: nowIso }))
    setUnreadCount(0)
    const { error } = await supabase
      .from('notifications').update({ read_at: nowIso })
      .eq('profile_id', user.id).is('read_at', null)
    if (error) {
      optimisticReadStampRef.current = 0
      console.warn('markAllRead failed; re-fetching', error)
      await fetchAll()
    }
  }

  const handleItemClick = async (n) => {
    setOpen(false)
    if (!n.read_at) markRead(n.id)              // fire-and-forget
    const target = navTargetFor(n)
    if (target) navigate(target)
  }

  // Bell renders nothing until we have a logged-in user — guards the
  // initial render before AuthContext resolves, and also lets the App
  // shell mount this unconditionally without leaking a UI element on
  // /login or unauthenticated /register/:formId.
  if (!user?.id) return null

  const badge = unreadCount > 9 ? '9+' : (unreadCount > 0 ? String(unreadCount) : null)

  return (
    <div ref={wrapperRef} className="flex-1 relative">
      {/* Bell — styled to match the BottomNav nav-item layout (flex-col,
          icon on top, label below). Mirrors the existing nav buttons in
          BottomNav.js so the bell sits as the 6th item without visual
          disruption. */}
      <button onClick={() => setOpen(o => !o)}
        aria-label="Notifications"
        className={`flex flex-col items-center pt-1 w-full transition-colors ${open ? 'text-teal-700' : 'text-stone-400'}`}>
        <span className="text-xl relative">
          🔔
          {badge && (
            <span className="absolute -top-1 -right-2 bg-red-500 text-white text-[10px] font-bold rounded-full px-1.5 min-w-[18px] text-center leading-tight py-0.5">
              {badge}
            </span>
          )}
        </span>
        <span className={`text-xs mt-0.5 ${open ? 'font-semibold' : ''}`}>Alerts</span>
      </button>

      {/* Dropdown — bottom-anchored sheet that opens UPWARD from the
          BottomNav. Sits above the nav bar (bottom-16 ≈ 64px from the
          viewport bottom; BottomNav itself is ~50px tall so this clears
          it). Right-anchored to align with the bell, which is the
          rightmost item in the 6-item nav row. */}
      {open && (
        <div className="fixed bottom-16 right-2 z-50 w-[calc(100vw-1rem)] max-w-md">
          <div className="bg-white rounded-2xl shadow-2xl border border-stone-200 max-h-[70vh] overflow-y-auto">
            {/* Header */}
            <div className="px-4 py-3 border-b border-stone-100 flex items-center justify-between sticky top-0 bg-white">
              <h2 className="text-sm font-bold text-gray-800">Notifications</h2>
              {unreadCount > 0 && (
                <button onClick={markAllRead}
                  className="text-xs text-teal-700 font-semibold hover:text-teal-800">
                  Mark all read
                </button>
              )}
            </div>

            {/* Items */}
            {loading && items.length === 0 ? (
              <div className="p-6 text-center text-stone-400 text-sm">Loading…</div>
            ) : items.length === 0 ? (
              // Empty state per §3.5
              <div className="p-8 text-center">
                <div className="text-3xl opacity-30 mb-2">🔔</div>
                <p className="text-sm text-stone-500">All caught up!</p>
              </div>
            ) : (
              <ul className="divide-y divide-stone-100">
                {items.map(n => {
                  const isUnread = !n.read_at
                  return (
                    <li key={n.id}>
                      <button onClick={() => handleItemClick(n)}
                        className={`w-full text-left px-4 py-3 hover:bg-stone-50 flex gap-3 items-start transition-colors ${isUnread ? 'bg-teal-50/40' : ''}`}>
                        {/* Unread dot */}
                        <span className="flex-shrink-0 mt-1">
                          {isUnread
                            ? <span className="block w-2 h-2 rounded-full bg-teal-600" aria-label="unread" />
                            : <span className="block w-2 h-2" aria-hidden="true" />}
                        </span>
                        {/* Icon */}
                        <span className="text-lg flex-shrink-0">{EVENT_ICON[n.event_type] || '🔔'}</span>
                        {/* Body */}
                        <div className="min-w-0 flex-1">
                          <p className={`text-sm text-gray-800 ${isUnread ? 'font-semibold' : ''}`}>{n.message}</p>
                          <p className="text-xs text-stone-400 mt-0.5">{relativeTime(n.created_at)}</p>
                        </div>
                      </button>
                    </li>
                  )
                })}
              </ul>
            )}

            {/* Cap notice — replace with "View all" footer + dedicated page in
                §12.3 future work. For now: just inform the user. */}
            {items.length >= ITEM_CAP && (
              <div className="px-4 py-2 text-xs text-stone-400 text-center border-t border-stone-100">
                Showing most recent {ITEM_CAP} — older items hidden
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
